import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'
import type { Lead, LeadFilters, LeadStats } from './types'
import { normalizeLead, type BackendLead } from './normalize'

// Re-export enrichment hook so consumers can import from a single location
export { useRunEnrichment } from './detail/use-lead-detail'

// Query keys
export const leadKeys = {
  all: ['leads'] as const,
  lists: () => [...leadKeys.all, 'list'] as const,
  list: (filters: LeadFilters) => [...leadKeys.lists(), filters] as const,
  details: () => [...leadKeys.all, 'detail'] as const,
  detail: (id: string) => [...leadKeys.details(), id] as const,
  stats: () => [...leadKeys.all, 'stats'] as const,
}

// Hooks

export function useLeads(filters: LeadFilters = {}) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: leadKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.status && filters.status !== 'all') params.set('status', filters.status)
      if (filters.search) params.set('search', filters.search)
      if (filters.score_min !== undefined) params.set('score_min', String(filters.score_min))
      if (filters.score_max !== undefined) params.set('score_max', String(filters.score_max))
      if (filters.campaign && filters.campaign !== 'all') params.set('campaign_id', filters.campaign)
      // Full-pipeline fetch (server max 500). Without an explicit limit the
      // server returns its default 50-row page, which rendered "Discovered
      // (50)" for a 268-lead pipeline and hid every status whose rows fell
      // outside that page.
      params.set('limit', '500')

      const path = `/api/leads?${params.toString()}`
      const res = await apiFetch<{ data: readonly BackendLead[]; total: number; has_more: boolean; cursor: string | null }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to fetch leads')
      return res.data.data.map(normalizeLead)
    },
    staleTime: 5_000,
    // 15s (was 5s): each poll now carries up to 500 rows with enrichment
    // blobs — a 5s cadence would stream hundreds of MB/hour from an idle tab.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  })
}

export function useLead(id: string) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: leadKeys.detail(id),
    queryFn: async () => {
      const res = await apiFetch<BackendLead>(`/api/leads/${id}`)
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to fetch lead ${id}`)
      return normalizeLead(res.data)
    },
    enabled: Boolean(id),
  })
}

// Shape returned by the backend stats endpoint
type BackendLeadStats = {
  readonly total: number
  readonly by_status: Record<string, number>
  readonly avg_icp_score?: number
  readonly avg_intent_score?: number
  readonly avg_composite_score?: number
}

export function useLeadStats() {
  const apiFetch = useApi()

  return useQuery<LeadStats>({
    queryKey: leadKeys.stats(),
    queryFn: async () => {
      const res = await apiFetch<BackendLeadStats>('/api/leads/stats')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to fetch lead stats')
      const raw = res.data
      const total = raw.total ?? 0
      const byStatus = (raw.by_status ?? {}) as Record<string, number>
      const replied = byStatus['replied'] ?? 0
      // 'won' is a frontend-only status alias; backend uses 'meeting_booked' as the terminal stage
      const won = byStatus['won'] ?? byStatus['meeting_booked'] ?? 0
      return {
        total,
        this_week: 0, // not available from backend
        reply_rate: total > 0 ? Math.round((replied / total) * 100) : 0,
        win_rate: total > 0 ? Math.round((won / total) * 100) : 0,
        by_status: { ...byStatus, won } as LeadStats['by_status'],
      }
    },
    staleTime: 5_000,
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  })
}

export function useCreateLead() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Omit<Lead, 'id' | 'created_at'>) => {
      // Map frontend field names to backend field names
      const body = { ...data, title: data.job_title, avatar_url: data.photo_url }
      const res = await apiFetch<BackendLead>('/api/leads', { method: 'POST', body })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to create lead')
      return normalizeLead(res.data)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
    },
  })
}

export function useCreateLeadByUrl() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (linkedinUrl: string) => {
      const res = await apiFetch<BackendLead>('/api/leads', {
        method: 'POST',
        body: { linkedin_url: linkedinUrl, source: 'manual' },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to create lead')
      return normalizeLead(res.data)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
    },
  })
}

export function useUpdateLead() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Lead> }) => {
      const res = await apiFetch<BackendLead>(`/api/leads/${id}`, { method: 'PUT', body: updates })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to update lead ${id}`)
      return normalizeLead(res.data)
    },
    onMutate: async ({ id, updates }) => {
      // Optimistic update: cancel in-flight queries and update cache
      await queryClient.cancelQueries({ queryKey: leadKeys.lists() })
      const previousLists = queryClient.getQueriesData<readonly Lead[]>({
        queryKey: leadKeys.lists(),
      })
      queryClient.setQueriesData<readonly Lead[]>(
        { queryKey: leadKeys.lists() },
        (old) => old?.map((l) => (l.id === id ? { ...l, ...updates } : l)),
      )
      return { previousLists }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data)
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] as const })
    },
  })
}

// Dashboard stats live under a different cache root (`['dashboard', ...]`).
// Lead delete must invalidate both so the dashboard's totalLeads / pipeline
// / activity panes update without waiting on their 30s refetch tick.
const DASHBOARD_KEY = ['dashboard'] as const

export function useDeleteLead() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch<void>(`/api/leads/${id}`, { method: 'DELETE' })
      if (!res.success) throw new Error(res.error ?? `Failed to delete lead ${id}`)
      return id
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY })
      queryClient.removeQueries({ queryKey: leadKeys.detail(id) })
    },
  })
}

export function useDeleteAllLeads() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const res = await apiFetch<{ deleted: number }>('/api/leads', { method: 'DELETE' })
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to delete all leads')
      }
      return res.data.deleted
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.all })
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY })
      queryClient.removeQueries({ queryKey: leadKeys.details() })
    },
  })
}

/**
 * Bulk delete by id. Hits the per-lead DELETE endpoint in parallel — the
 * backend does not yet expose a `DELETE /leads?ids=...` route. Returns the
 * list of ids that succeeded so the caller can clear selection precisely.
 */
export function useBulkDeleteLeads() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          apiFetch<void>(`/api/leads/${id}`, { method: 'DELETE' }).then((res) => {
            if (!res.success) throw new Error(res.error ?? `Failed to delete ${id}`)
            return id
          }),
        ),
      )
      const succeeded = results
        .map((r, i) => (r.status === 'fulfilled' ? ids[i]! : null))
        .filter((v): v is string => v !== null)
      const failed = results.length - succeeded.length
      return { succeeded, failed }
    },
    onSuccess: ({ succeeded }) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY })
      for (const id of succeeded) queryClient.removeQueries({ queryKey: leadKeys.detail(id) })
    },
  })
}

/**
 * Bulk-create leads from a list of LinkedIn URLs (e.g. extracted from a
 * search-results page). The backend route accepts only profile URLs at
 * present; un-resolvable URLs are skipped server-side and reported back.
 */
export function useImportLeadsFromUrls() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { search_url: string }) => {
      const res = await apiFetch<{
        created: number
        skipped: number
        already_existed: number
        leads: readonly BackendLead[]
        errors?: readonly string[]
      }>('/api/leads/import-search-url', {
        method: 'POST',
        body: input,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to import leads')
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY })
    },
  })
}
